import Tooltip from '@/components/UI/Tooltip';
import cn from 'classnames';
import type { FC } from 'react';
import type { DataTableCellIndexCoordinate, DataTableColumnType } from '@/components/UI/DataTableV2/DataTable.model';
import styles from '../DataTableV2.module.scss';
import { useDataTable } from '../DataTableProvider';
import { useCellAlerts } from '../hooks/useCellAlerts';
import { useCellCoordinate } from '../hooks/useCellCoordinate';
import { useCellData } from '../hooks/useCellData';
import { useCellMouseHandlers } from '../hooks/useCellMouseHandlers';
import { useCellPositioning } from '../hooks/useCellPositioning';
import { useCellSelectionState } from '../hooks/useCellSelectionState';
import { useCellState } from '../hooks/useCellState';
import { DataTableCellValueRenderer } from './DataTableCellValueRenderer';
import { _notNil } from '@/littledash';

const shouldFormatValue = (columnType: DataTableColumnType | undefined): boolean =>
  columnType !== 'text' && columnType !== 'observation';

interface DataTableReadonlyCellProps {
  coordinate: DataTableCellIndexCoordinate;
  positionOffset: { x: number; y: number };
}

export const DataTableReadonlyCell: FC<DataTableReadonlyCellProps> = ({ coordinate, positionOffset }) => {
  const { dataTableService } = useDataTable();

  // Foundation
  const { idCoordinate, cellRef } = useCellCoordinate(coordinate);

  // Data & State
  const { cellData } = useCellData(idCoordinate, cellRef);
  const { cellState, tooltip } = useCellState(idCoordinate, cellRef);
  const { selectionState } = useCellSelectionState(coordinate, idCoordinate);
  const { hasAlert } = useCellAlerts(idCoordinate, cellRef);

  // Interaction
  const { handleMouseDown, handleMouseEnter, handleMouseUp } = useCellMouseHandlers(coordinate);

  // Styling
  const { containerStyle } = useCellPositioning(positionOffset);

  const column = _notNil(idCoordinate) ? dataTableService.columnById(idCoordinate.column) : undefined;
  const cellType = column?.type ?? 'unknown';
  const hasGroupBorder = dataTableService.rightBorderType(coordinate.column) === 'group-separator';

  return (
    <div
      className={cn(styles.CellContainer, { [styles.groupBorder]: hasGroupBorder })}
      style={containerStyle}
      onMouseDown={handleMouseDown}
      onMouseEnter={handleMouseEnter}
      onMouseUp={handleMouseUp}
    >
      {cellState === 'Loading' ? (
        <div className={cn(styles.Cell, styles.Loading)} />
      ) : (
        <Tooltip render={tooltip}>
          <div
            className={cn(styles.Cell, styles[selectionState], 'truncate', styles[cellState], styles.ReadonlyColumn)}
            data-celltype={cellType}
            data-state={cellState}
            style={{ height: '100%' }}
          >
            <span>
              {shouldFormatValue(column?.type) ? (
                <DataTableCellValueRenderer value={cellData?.value} />
              ) : (
                (cellData?.value ?? '')
              )}
            </span>
          </div>
        </Tooltip>
      )}
      {hasAlert && <div className={styles.CellAlert} />}
    </div>
  );
};
